import { useState } from "react";
import {
  Container,
  Card,
  Table,
  Badge,
  Text,
  Anchor,
  ActionIcon,
  Group,
  TextInput,
  Tooltip,
  ScrollArea,
} from "@mantine/core";
import {
  IconPlayerStop,
  IconEraser,
  IconTrash,
  IconSearch,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { heraldApi } from "../lib/api";
import { HeraldTask } from "../types";
import { PageHeader } from "../components/PageHeader";
import { PageTransition } from "../components/PageTransition";
import { EmptyState } from "../components/EmptyState";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { ConfirmationModal } from "../components/ConfirmationModal";
import { HeraldTaskDetailsModal } from "../components/HeraldTaskDetailsModal";
import { showSuccess, showError } from "../lib/notifications";

const STATUS_COLOR: Record<HeraldTask["status"], string> = {
  running: "blue",
  waiting_for_input: "yellow",
  succeeded: "teal",
  failed: "red",
  cancelled: "gray",
};

function timeAgo(unixSeconds: number): string {
  if (!unixSeconds) return "—";
  const diff = Math.max(0, Math.floor(Date.now() / 1000) - unixSeconds);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function HeraldTasksPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [cleanupTarget, setCleanupTarget] = useState<HeraldTask | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<HeraldTask | null>(null);
  const [detailsTarget, setDetailsTarget] = useState<HeraldTask | null>(null);

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ["herald-tasks"],
    queryFn: async () => {
      const res = await heraldApi.listTasks();
      return res.data;
    },
    refetchInterval: 5000,
  });

  const cancelMutation = useMutation({
    mutationFn: (taskId: string) => heraldApi.cancelTask(taskId),
    onSuccess: () => {
      showSuccess("Cancel requested — Herald will stop the run shortly.");
      queryClient.invalidateQueries({ queryKey: ["herald-tasks"] });
    },
    onError: (error) => showError(error),
  });

  const cleanupMutation = useMutation({
    mutationFn: (taskId: string) => heraldApi.cleanupTask(taskId),
    onSuccess: (res) => {
      if (res.data.warning) {
        showError(res.data.warning, "Cleaned up, with a warning");
      } else {
        showSuccess("Task cleaned up.");
      }
      setCleanupTarget(null);
      queryClient.invalidateQueries({ queryKey: ["herald-tasks"] });
    },
    onError: (error) => showError(error),
  });

  const deleteMutation = useMutation({
    mutationFn: (taskId: string) => heraldApi.deleteTask(taskId),
    onSuccess: () => {
      showSuccess("Task deleted.");
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: ["herald-tasks"] });
    },
    onError: (error) => showError(error),
  });

  const filtered = tasks.filter((t) =>
    search
      ? t.repo.toLowerCase().includes(search.toLowerCase()) ||
        t.prompt.toLowerCase().includes(search.toLowerCase()) ||
        t.triggered_by.toLowerCase().includes(search.toLowerCase())
      : true
  );
  const sorted = [...filtered].sort((a, b) => b.created_at - a.created_at);

  return (
    <Container fluid p={0} pos="relative">
      <PageTransition>
        <PageHeader
          title="Herald"
          description="Prompt-to-code agent tasks — triggered from Mattermost, monitored here."
        />

        {isLoading ? (
          <LoadingOverlay message="Loading..." />
        ) : tasks.length === 0 ? (
          <EmptyState
            title="No Herald tasks yet"
            description="Tasks created via the /fix Mattermost command will show up here."
          />
        ) : (
          <Card mt="md" shadow="sm" radius="md" withBorder>
            <Group p="md" position="apart" align="center">
              <TextInput
                placeholder="Search tasks..."
                icon={<IconSearch size={16} />}
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                sx={(theme) => ({
                  width: 300,
                  [theme.fn.smallerThan("sm")]: { width: "100%" },
                })}
              />
            </Group>

            <ScrollArea>
              <Table striped highlightOnHover>
                <thead>
                  <tr>
                    <th>Repo</th>
                    <th>Prompt</th>
                    <th>Status</th>
                    <th>Triggered by</th>
                    <th>Created</th>
                    <th>Links</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((task) => (
                    <tr
                      key={task.task_id}
                      style={{ cursor: "pointer" }}
                      onClick={() => setDetailsTarget(task)}
                    >
                      <td>
                        <Text size="sm" weight={500}>
                          {task.repo}
                        </Text>
                      </td>
                      <td style={{ maxWidth: 320 }}>
                        <Tooltip
                          label={task.prompt}
                          disabled={!task.prompt}
                          multiline
                          width={320}
                        >
                          <Text size="sm" lineClamp={2}>
                            {task.prompt || "—"}
                          </Text>
                        </Tooltip>
                      </td>
                      <td>
                        <Badge color={STATUS_COLOR[task.status] || "gray"}>
                          {task.status.replace(/_/g, " ")}
                        </Badge>
                      </td>
                      <td>
                        <Text size="sm">{task.triggered_by || "—"}</Text>
                      </td>
                      <td>
                        <Text size="sm" color="dimmed">
                          {timeAgo(task.created_at)}
                        </Text>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <Group spacing={6}>
                          {task.preview_url && (
                            <Anchor
                              href={task.preview_url}
                              target="_blank"
                              size="sm"
                            >
                              preview
                            </Anchor>
                          )}
                          {task.pr_url && (
                            <Anchor href={task.pr_url} target="_blank" size="sm">
                              PR
                            </Anchor>
                          )}
                          {!task.preview_url && !task.pr_url && (
                            <Text size="sm" color="dimmed">
                              —
                            </Text>
                          )}
                        </Group>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <Group spacing={4} noWrap>
                          {task.status === "running" && (
                            <Tooltip label="Cancel">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                loading={cancelMutation.isLoading}
                                onClick={() =>
                                  cancelMutation.mutate(task.task_id)
                                }
                              >
                                <IconPlayerStop size={16} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                          {!task.cleaned_at &&
                            ["succeeded", "failed", "cancelled"].includes(
                              task.status
                            ) && (
                              <Tooltip label="Clean up">
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  onClick={() => setCleanupTarget(task)}
                                >
                                  <IconEraser size={16} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                          <Tooltip label="Delete">
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              onClick={() => setDeleteTarget(task)}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ScrollArea>
          </Card>
        )}
      </PageTransition>

      <ConfirmationModal
        opened={!!cleanupTarget}
        onClose={() => setCleanupTarget(null)}
        onConfirm={() =>
          cleanupTarget && cleanupMutation.mutate(cleanupTarget.task_id)
        }
        title="Clean up task"
        message={`This removes the preview container, image, and worktree for "${cleanupTarget?.repo}". The task's history stays visible.`}
        confirmLabel="Clean up"
        isLoading={cleanupMutation.isLoading}
      />

      <ConfirmationModal
        opened={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() =>
          deleteTarget && deleteMutation.mutate(deleteTarget.task_id)
        }
        title="Delete task"
        message={`This permanently removes the task record for "${deleteTarget?.repo}" (${deleteTarget?.task_id.slice(
          0,
          8
        )}), cleaning up its container/image/worktree first if that hasn't happened yet. This can't be undone.`}
        confirmLabel="Delete"
        isLoading={deleteMutation.isLoading}
      />

      <HeraldTaskDetailsModal
        task={detailsTarget}
        opened={!!detailsTarget}
        onClose={() => setDetailsTarget(null)}
      />
    </Container>
  );
}
